import { z } from 'zod'
import { NabooError } from '@/core/infrastructure/services/NabooError'

export class EntityValidator {

  static validateAndCreateFactory<T, E extends z.ZodTypeAny>(
    schema: E,
    data: unknown,
    build: (data: z.output<E>, errorContext: string) => T,
    errorContext: string,
  ): T {
    const parseResult = schema.safeParse(data) as z.output<E>
    if (parseResult.success) {
      try {
        return build(parseResult.data, errorContext)
      } catch (error) {
        console.error(`Error creating ${errorContext} instance:`, error)
        throw new NabooError({
          message: `Erreur lors de la création de l'instance ${errorContext}`,
          status: 500,
          name: 'EntityCreationError',
          code: 'ENTITY_CREATION_ERROR',
          details: error
        })
      }
    } else {
      console.error(`${errorContext} validation errors:`, parseResult.error)
      throw new NabooError({
        message: `Les données pour ${errorContext} ne sont pas valides`,
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR',
        details: parseResult.error
      })
    }
  }

  /**
   * Valide des données avec un schéma Zod et crée une instance de classe
   * @param schema Le schéma Zod à utiliser pour la validation
   * @param data Les données à valider
   * @param EntityClass La classe à instancier avec les données validées
   * @param errorContext Contexte pour l'erreur en cas d'échec
   */
  static validateAndCreate<T, E extends z.ZodTypeAny>(
    schema: E,
    data: unknown,
    EntityClass: new (data: z.output<E>) => T,
    errorContext: string,
  ): T {
    return EntityValidator.validateAndCreateFactory(schema, data, (data: E) => new EntityClass(data), errorContext)
  }

  /**
   * Valide et crée plusieurs instances à partir d'un tableau de données
   */
  static validateAndCreateMany<T, E>(
    schema: z.ZodType<E[]>,
    data: unknown,
    EntityClass: new (data: E) => T,
    errorContext: string
  ): T[] {
    return EntityValidator.validateAndCreateFactory(schema, data, (data) =>
      data.map((item) => new EntityClass(item)), errorContext)
  }

  /**
   * Valide les données avec un schéma Zod
   * @param schema Le schéma Zod à utiliser pour la validation
   * @param data Les données à valider
   * @param errorContext Contexte pour l'erreur en cas d'échec
   */
  static validate<T>(schema: z.ZodType<T>, data: unknown, errorContext: string): T {
    const parseResult = schema.safeParse(data)

    if (parseResult.success) {
      return parseResult.data
    } else {
      console.error(`${errorContext} validation errors:`, parseResult.error.format())
      throw new NabooError({
        message: `Les données pour ${errorContext} ne sont pas valides`,
        status: 422,
        name: 'ValidationError',
        code: 'SCHEMA_VALIDATION_ERROR',
        details: parseResult.error
      })
    }
  }
}
